import {
  FormFieldSection,
  FormFields,
} from 'app/modules/filingFormGenerator/models';
import {
  dateField,
  prefixFields,
  selectField,
  textField,
  textareaField,
} from 'app/modules/goAML/formConfig/fields/fieldBuilders';
import {
  getAccountFields,
  getAccountMyClientFields,
} from 'app/modules/goAML/formConfig/fields/accountFields';
import {
  getEntityFields,
  getEntityMyClientFields,
} from 'app/modules/goAML/formConfig/fields/entityFields';
import { getPersonTransactionFields } from 'app/modules/goAML/formConfig/fields/personFields';
import { GoamlReferenceTables } from 'app/modules/goAML/queries/useGetGoamlFiuConfig';

type FieldValues = Record<string, any>;

const getTransactionRawFields = (
  referenceTables: GoamlReferenceTables,
): FormFields<FieldValues> => [
  textField({
    name: 'transactionnumber',
    label: 'Transaction number',
    required: true,
  }),
  textField({
    name: 'internal_ref_number',
    label: 'Internal reference number',
  }),
  textField({
    name: 'transaction_location',
    label: 'Transaction location',
    required: true,
  }),
  dateField({
    name: 'date_transaction',
    label: 'Transaction date',
    required: true,
  }),
  textField({ name: 'teller', label: 'Teller' }),
  textField({ name: 'authorized', label: 'Authorized by' }),
  selectField(
    {
      name: 'transmode_code',
      label: 'Transaction mode',
      required: true,
      refTable: 'transmode_code',
    },
    referenceTables,
  ),
  textField({ name: 'transmode_comment', label: 'Transaction mode comment' }),
  textField({ name: 'amount_local', label: 'Local amount', required: true }),
  textareaField({
    name: 'transaction_description',
    label: 'Transaction description',
    required: true,
  }),
  dateField({ name: 'value_date', label: 'Value date' }),
];

const FROM_TYPE_FIELD = selectField({
  name: 'from_type',
  label: 'Sender type',
  required: true,
  options: [
    { value: 't_from.from_account', text: 'From account' },
    {
      value: 't_from_my_client.from_account',
      text: 'From account (my client)',
    },
    { value: 't_from.from_person', text: 'From person' },
    {
      value: 't_from_my_client.from_person',
      text: 'From person (my client)',
    },
    { value: 't_from.from_entity', text: 'From entity' },
    {
      value: 't_from_my_client.from_entity',
      text: 'From entity (my client)',
    },
  ],
});

const TO_TYPE_FIELD = selectField({
  name: 'to_type',
  label: 'Receiver type',
  required: true,
  options: [
    { value: 't_to.to_account', text: 'To account' },
    { value: 't_to_my_client.to_account', text: 'To account (my client)' },
    { value: 't_to.to_person', text: 'To person' },
    { value: 't_to_my_client.to_person', text: 'To person (my client)' },
    { value: 't_to.to_entity', text: 'To entity' },
    { value: 't_to_my_client.to_entity', text: 'To entity (my client)' },
  ],
});

const getFromDetailsFields = (
  referenceTables: GoamlReferenceTables,
): FormFields<FieldValues> => [
  selectField(
    {
      name: 'from_details.from_funds_code',
      label: 'Funds type',
      required: true,
      refTable: 'funds_type',
    },
    referenceTables,
  ),
  textField({
    name: 'from_details.from_funds_comment',
    label: 'Funds comment',
  }),
  selectField(
    {
      name: 'from_details.from_country',
      label: 'From country',
      required: true,
      defaultValue: 'US',
      refTable: 'country_code',
    },
    referenceTables,
  ),
];

const getToDetailsFields = (
  referenceTables: GoamlReferenceTables,
): FormFields<FieldValues> => [
  selectField(
    {
      name: 'to_details.to_funds_code',
      label: 'Funds type',
      required: true,
      refTable: 'funds_type',
    },
    referenceTables,
  ),
  textField({ name: 'to_details.to_funds_comment', label: 'Funds comment' }),
  selectField(
    {
      name: 'to_details.to_country',
      label: 'To country',
      required: true,
      defaultValue: 'US',
      refTable: 'country_code',
    },
    referenceTables,
  ),
];

interface PartySectionDef {
  key: string;
  label: string;
  typeField: string;
  typeValue: string;
  getFields: (rt: GoamlReferenceTables) => FormFields<FieldValues>;
}

const PARTY_SECTIONS: PartySectionDef[] = [
  {
    key: 'from_account',
    label: 'From account',
    typeField: 'from_type',
    typeValue: 't_from.from_account',
    getFields: getAccountFields,
  },
  {
    key: 'from_account_my_client',
    label: 'From account (my client)',
    typeField: 'from_type',
    typeValue: 't_from_my_client.from_account',
    getFields: getAccountMyClientFields,
  },
  {
    key: 'from_person',
    label: 'From person',
    typeField: 'from_type',
    typeValue: 't_from.from_person',
    getFields: getPersonTransactionFields,
  },
  {
    key: 'from_person_my_client',
    label: 'From person (my client)',
    typeField: 'from_type',
    typeValue: 't_from_my_client.from_person',
    getFields: (rt) => getPersonTransactionFields(rt, { myClient: true }),
  },
  {
    key: 'from_entity',
    label: 'From entity',
    typeField: 'from_type',
    typeValue: 't_from.from_entity',
    getFields: getEntityFields,
  },
  {
    key: 'from_entity_my_client',
    label: 'From entity (my client)',
    typeField: 'from_type',
    typeValue: 't_from_my_client.from_entity',
    getFields: getEntityMyClientFields,
  },
  {
    key: 'to_account',
    label: 'To account',
    typeField: 'to_type',
    typeValue: 't_to.to_account',
    getFields: getAccountFields,
  },
  {
    key: 'to_account_my_client',
    label: 'To account (my client)',
    typeField: 'to_type',
    typeValue: 't_to_my_client.to_account',
    getFields: getAccountMyClientFields,
  },
  {
    key: 'to_person',
    label: 'To person',
    typeField: 'to_type',
    typeValue: 't_to.to_person',
    getFields: getPersonTransactionFields,
  },
  {
    key: 'to_person_my_client',
    label: 'To person (my client)',
    typeField: 'to_type',
    typeValue: 't_to_my_client.to_person',
    getFields: (rt) => getPersonTransactionFields(rt, { myClient: true }),
  },
  {
    key: 'to_entity',
    label: 'To entity',
    typeField: 'to_type',
    typeValue: 't_to.to_entity',
    getFields: getEntityFields,
  },
  {
    key: 'to_entity_my_client',
    label: 'To entity (my client)',
    typeField: 'to_type',
    typeValue: 't_to_my_client.to_entity',
    getFields: getEntityMyClientFields,
  },
];

const buildPartySection = (
  party: PartySectionDef,
  referenceTables: GoamlReferenceTables,
): FormFieldSection<FieldValues> => ({
  type: 'section',
  name: party.key,
  label: party.label,
  bindFieldsToParentPath: true,
  mustExistInPayload: false,
  mustHaveValueIfExist: false,
  dependentPaths: (sectionIndex) => [
    `transaction[${sectionIndex}].${party.typeField}`,
  ],
  hideIfTrue: {
    and: [
      {
        path: (sectionIndex) =>
          `transaction[${sectionIndex}].${party.typeField}`,
        conditions: (value) => value !== party.typeValue,
      },
    ],
  },
  fields: prefixFields(party.key, party.getFields(referenceTables)),
});

export const getTransactionFields = (
  referenceTables: GoamlReferenceTables,
): FormFields<FieldValues> => [
  ...getTransactionRawFields(referenceTables),
  FROM_TYPE_FIELD,
  ...getFromDetailsFields(referenceTables),
  ...PARTY_SECTIONS.filter((p) => p.typeField === 'from_type').map((p) =>
    buildPartySection(p, referenceTables),
  ),
  TO_TYPE_FIELD,
  ...getToDetailsFields(referenceTables),
  ...PARTY_SECTIONS.filter((p) => p.typeField === 'to_type').map((p) =>
    buildPartySection(p, referenceTables),
  ),
];
